<script setup lang="ts">
import { useMenuItemSelectedStore } from "~/stores/MenuItemSelected"
import { useCategoriesStore } from "~/stores/categoriesStore"
import { useCurrentPageStore } from "~/stores/currentPageStore"
import { useShopListStore } from "~/stores/shopListStore"
import type { HomeBreadCrumbItem } from "~/components/HomeBreadCrumbs.vue"
import { useOFetchCustom } from "~/composables/useOFetchCustom"
import {
    type SUPPORTED_PAGES,
    SUPPORTED_PAGES_ENUM,
} from "~/common/breadCrumbs"
import ButtonInfoContact from "~/components/ButtonInfoContact.vue"
import DropdownCard from "~/components/DropdownCard.vue"
import CustomTreeFilter from "~/components/CustomTreeFilter.vue"
import PartnerTopBar from "~/components/Partner/PartnerTopBar.vue"
import PartnerNav from "~/components/Partner/PartnerNav.vue"
import PartnerPreFooter from "~/components/Partner/PartnerPreFooter.vue"
import { usePartnerBranding } from "~/composables/usePartnerBranding"
import { goToRoute } from "~/common/helpers"
import { useProvinceSelection } from "~/composables/useProvinceSelection"
import type { ShopSelectItem } from "~/models/Shop"

/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    title?: string
    subtitle?: string
    withoutPreNav?: boolean
    withoutBanner?: boolean
    withoutBreadCrumbs?: boolean
    breadCrumbs?: HomeBreadCrumbItem[]
    currentPage?: SUPPORTED_PAGES
    lastBreadCrumbName?: string
    withoutParentStyles?: boolean
    parentClass?: string
    withoutSteps?: boolean
    customBanner?: string
    customBgBanner?: string
    bannerClass?: string
    withoutPreFooter?: boolean
    breadCrumbsItems?: HomeBreadCrumbItem[]
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const router = useRouter()
const route = useRoute()
const menuItemSelectedStore = useMenuItemSelectedStore()
const shopCartStore = useShopCartStore()
const initSettingsStore = useDefaultSettingsStore()
const shopListStore = useShopListStore()
const { isPartnerMode } = usePartnerBranding()
const { data, get } = useOFetchCustom("init-settings")
const currentPageComputed = computed(() => props.currentPage)
const {
    breadCrumbs,
    addBreadCrumbsItem,
    addSomeBreadCrumbItems,
    build: buildBreadCrumbs,
} = useBreadCrumbs(currentPageComputed.value)
const currentPageStore = useCurrentPageStore()
const categoriesStore = useCategoriesStore()
const queryStore = useSearchStore()
const isMenuOpen = ref(false)

// Province selection functionality
const { t: $t } = useI18n()
const { showProvinceModal, selectedProvince } = useProvinceSelection()

// Open province modal function
const openProvinceModal = () => {
    showProvinceModal.value = true
}

// Display province name computed property
const displayProvinceName = computed(() => {
    if (!selectedProvince.value) {
        return $t("province_selection.select_province")
    }
    if (selectedProvince.value === $t("province_selection.none_option")) {
        return $t("province_selection.no_province_selected")
    }
    return selectedProvince.value
})

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(async () => {
    if (route.query.category) {
        categoriesStore.setCurrentCategorySelected(
            route.query.category as string
        )
    }
    categoriesStore.getCategories()
    categoriesStore.getCategoriesByStore(
        shopListStore.currentShop?.slug ?? undefined
    )

    currentPageStore.setCurrentPage(
        props.currentPage || SUPPORTED_PAGES_ENUM.Home
    )
    get(undefined, {
        onSuccess: (res) => {
            initSettingsStore.setSettings(res.data)
        },
    })
    shopListStore.fetchShopList()
    shopCartStore.fetchShopCart()
    const route_name = router.currentRoute.value.name

    if (route_name) {
        menuItemSelectedStore.setByRoute(route_name.toString())
    }

    if (props.breadCrumbs && props.breadCrumbs.length > 0) {
        breadCrumbs.value = props.breadCrumbs
        buildBreadCrumbs(breadCrumbs.value)
    }

    if (props.lastBreadCrumbName) {
        addBreadCrumbsItem(props.lastBreadCrumbName)
    }

    if (props.breadCrumbsItems && props.breadCrumbsItems.length > 0) {
        addSomeBreadCrumbItems(props.breadCrumbsItems)
    }
})

watch(
    () => props.breadCrumbsItems,
    (newValue: any) => {
        if (newValue && newValue.length > 0) {
            addSomeBreadCrumbItems(newValue)
        }
    },
    { deep: true }
)

watch(
    () => props.lastBreadCrumbName,
    (newValue: any) => {
        if (newValue) {
            addBreadCrumbsItem(newValue)
        }
    }
)

watch(
    () => props.breadCrumbs,
    (newValue: any) => {
        if (newValue) {
            breadCrumbs.value = newValue
            buildBreadCrumbs(breadCrumbs.value)
        }
    }
)

watch(
    () => shopListStore.currentShop,
    (newValue) => {
        if (newValue) {
            categoriesStore.fetchCategoriesByStore(newValue.slug)
        } else {
            categoriesStore.getCategories()
        }
    },
    {
        flush: "post",
    }
)

/**
 *
 *  Functions section
 *
 *
 */

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
}

const goToOffers = () => {
    router.push({ path: "/offers" })
}

const searchByCategory = (category: string) => {
    categoriesStore.setCurrentCategorySelected(category)
    router.push({
        path: "/store",
        query: { ...route.query, category: category },
    })
}

const goToStore = (shop?: ShopSelectItem) => {
    if (shop) {
        shopListStore.fetchCurrentShop(shop)
        router.push({ path: `/store/${shop.slug}` })
    } else {
        shopListStore.clearCurrentShop()
        router.push({ path: "/store" })
    }
}
</script>

<template>
    <q-layout view="hHh lpR fff">
        <q-drawer
            v-model="isMenuOpen"
            :width="300"
            :breakpoint="700"
            elevated
            overlay
            behavior="mobile"
            class="tw-bg-white tw-text-secondary"
        >
            <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
                <div @click="goToRoute('/')">
                    <ApplicationLogo
                        class="tw-w-[120px] tw-h-[85px] tw-mx-auto"
                    />
                </div>
                <q-separator class="tw-h-[2px]"></q-separator>
                <ProfileMenu
                    v-if="!isPartnerMode"
                    class="!tw-flex !tw-flex-col !tw-mx-auto"
                />

                <q-separator class="tw-h-[2px]"></q-separator>

                <!-- Province Selection Button for Mobile -->
                <q-btn
                    class="tw-capitalize tw-font-titilium tw-rounded-full tw-text-base tw-font-bold !tw-border-0 tw-bg-primary hover:tw-bg-primary-800 !tw-px-4 !tw-py-2 tw-text-white tw-mx-2"
                    flat
                    @click="openProvinceModal"
                >
                    <div
                        class="tw-flex tw-items-center tw-w-full tw-justify-between tw-text-inherit tw-gap-2"
                    >
                        <q-icon name="location_on" size="18px" />
                        <q-item-label
                            class="tw-px-auto tw-font-titilium tw-flex-1 tw-text-left"
                        >
                            {{ displayProvinceName }}
                        </q-item-label>
                        <q-icon name="keyboard_arrow_down" size="18px" />
                    </div>
                </q-btn>

                <q-separator class="tw-h-[2px]"></q-separator>

                <button
                    @click="goToOffers()"
                    class="tw-cursor-pointer tw-w-full tw-font-titilium tw-items-center tw-text-white tw-self-center tw-flex tw-bg-secondary tw-h-16 tw-gap-1 tw-justify-around tw-flex-wrap"
                >
                    <img src="/public/img/offer.svg" alt="offer" />
                    <span class="tw-font-bold tw-text-xl">{{
                        $t("home.products_in_offer")
                    }}</span>
                    <q-icon name="fa-solid fa-arrow-right" size="20px"></q-icon>
                </button>

                <DropdownCard
                    v-if="shopListStore.shopList.length > 0"
                    :title="$t('fields.stores')"
                    opened
                >
                    <div class="tw-flex tw-flex-col tw-gap-3">
                        <p
                            :class="`${!shopListStore.currentShop ? 'tw-text-primary' : 'tw-text-customGray-400'} tw-cursor-pointer tw-font-poppins tw-ml-4`"
                            @click="goToStore()"
                        >
                            Todas
                        </p>
                        <p
                            v-for="shop in shopListStore.shopList"
                            :key="shop.slug"
                            :class="`${shopListStore.currentShop?.slug === shop.slug ? 'tw-text-primary' : 'tw-text-customGray-400'} tw-cursor-pointer tw-font-poppins tw-ml-4`"
                            @click="goToStore(shop)"
                        >
                            {{ shop.name }}
                        </p>
                    </div>
                </DropdownCard>

                <DropdownCard
                    v-if="categoriesStore.categories"
                    :title="$t('categories.title')"
                    opened
                >
                    <CustomTreeFilter
                        :options="categoriesStore.categories"
                        @onSelect="searchByCategory"
                    />
                </DropdownCard>

                <slot name="mobile-left-drawer"></slot>
            </div>
        </q-drawer>
        <slot name="header">
            <q-header class="!tw-bg-transparent">
                <PartnerTopBar v-if="isPartnerMode" />
                <slot name="nav">
                    <HomeNav @toggleMenu="toggleMenu">
                        <template #pre-nav>
                            <HomePreNav
                                class="tw-hidden md:tw-block"
                                v-if="!withoutPreNav"
                            />
                        </template>
                        <template #mobile-left-drawer>
                            <slot name="mobileLeftDrawer"></slot>
                        </template>
                    </HomeNav>
                </slot>
            </q-header>
        </slot>
        <q-page-container>
            <q-page>
                <template v-if="!withoutBanner">
                    <slot name="banner">
                        <HomeBanner
                            :customBanner="props.customBanner"
                            :title="title || ''"
                            :subtitle="subtitle"
                            :customBgBanner="props.customBgBanner"
                            :withoutBackground="
                                !!bannerClass && bannerClass !== ''
                            "
                            :class="bannerClass"
                        />
                    </slot>
                </template>
                <template v-if="!withoutBreadCrumbs">
                    <HomeBreadCrumbs :items="breadCrumbs" />
                </template>
                <slot name="main">
                    <slot name="pre_main"></slot>
                    <div
                        :class="`${!withoutParentStyles && `${parentClass} tw-py-20 max-container`}`"
                    >
                        <slot name="offers"></slot>
                        <slot></slot>
                    </div>
                </slot>
                <slot name="post_main">
                    <slot name="most_sold"></slot>
                </slot>
                <ButtonInfoContact></ButtonInfoContact>
            </q-page>
        </q-page-container>

        <footer class="footer">
            <PartnerPreFooter v-if="isPartnerMode" />
            <HomePreFooter v-if="!withoutPreFooter" />
            <HomeFooter />
        </footer>
    </q-layout>
</template>
